import { Controller } from "@hotwired/stimulus"

import { AUTOPLAY_INTERVAL_MS, indexAt, nextIndex, previousIndex, shouldAutoplay } from "../carousel/rotation"

type Hold = "pointer" | "focus" | "hidden"

// The hero carousel of the kennel's home (full phase 102,
// `public/shared/_home_carousel`, `doc/site-layout.md` 5.4.2). Without it the
// photos are a row the visitor scrolls, with the controls `hidden`; this
// controller shows the controls, moves one photo to a view and keeps the dots
// and the slides the assistive technology reads in step with the row.
//
// It turns on its own every six seconds only with more than one photo and
// never under `prefers-reduced-motion: reduce`, where the pause button is
// hidden and the rest of the controls stay. It holds still while the pointer
// is over it, while focus is inside it and while the tab is hidden, and stops
// for good once the visitor presses «Пауза», any other control or takes the
// row in hand; «Продолжить» turns it again. The track is `aria-live="off"`
// while it turns by itself and `polite` after a move the visitor made, so a
// screen reader names the photo it moved to and is not interrupted every six
// seconds.
export default class HomeCarouselController extends Controller<HTMLElement> {
  static targets = ["track", "slide", "controls", "pause", "dot"]

  declare readonly trackTarget: HTMLElement
  declare readonly slideTargets: HTMLElement[]
  declare readonly controlsTarget: HTMLElement
  declare readonly hasControlsTarget: boolean
  declare readonly pauseTarget: HTMLButtonElement
  declare readonly hasPauseTarget: boolean
  declare readonly dotTargets: HTMLButtonElement[]

  private index = 0
  private stopped = false
  private timer: number | null = null
  // The photo a move scrolls to: the row passes the photos between on the
  // way, and those are not current, or the dots flicker and a polite region
  // reads every photo passed. A finger, or a second after the move, lets go.
  private target: number | null = null
  private targetTimer: number | null = null
  private readonly holds = new Set<Hold>()
  private motion: MediaQueryList | null = null

  connect(): void {
    if (!this.hasControlsTarget || this.slideTargets.length < 2) return

    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.motion.addEventListener("change", this.motionChanged)
    this.element.addEventListener("mouseenter", this.pointerEntered)
    this.element.addEventListener("mouseleave", this.pointerLeft)
    this.element.addEventListener("focusin", this.focusEntered)
    this.element.addEventListener("focusout", this.focusLeft)
    this.trackTarget.addEventListener("pointerdown", this.takenInHand)
    document.addEventListener("visibilitychange", this.visibilityChanged)
    if (document.hidden) this.holds.add("hidden")

    this.controlsTarget.hidden = false
    this.index = indexAt(this.trackTarget.scrollLeft, this.slideWidth(), this.slideTargets.length)
    this.mark()
    this.motionChanged()
  }

  disconnect(): void {
    this.clearTimer()
    this.letGo()
    this.motion?.removeEventListener("change", this.motionChanged)
    this.motion = null
    this.element.removeEventListener("mouseenter", this.pointerEntered)
    this.element.removeEventListener("mouseleave", this.pointerLeft)
    this.element.removeEventListener("focusin", this.focusEntered)
    this.element.removeEventListener("focusout", this.focusLeft)
    this.trackTarget.removeEventListener("pointerdown", this.takenInHand)
    document.removeEventListener("visibilitychange", this.visibilityChanged)
    this.holds.clear()
    if (!this.hasControlsTarget) return

    this.controlsTarget.hidden = true
    this.trackTarget.setAttribute("aria-live", "polite")
    for (const slide of this.slideTargets) slide.removeAttribute("aria-hidden")
  }

  previous(): void {
    this.stop()
    this.go(previousIndex(this.index, this.slideTargets.length))
  }

  next(): void {
    this.stop()
    this.go(nextIndex(this.index, this.slideTargets.length))
  }

  show(event: Event & { params?: { index?: number } }): void {
    this.stop()
    this.go(Number(event.params?.index ?? 0))
  }

  toggle(): void {
    if (this.stopped) {
      this.stopped = false
      this.holds.delete("focus")
      this.paintPause()
      this.schedule()
    } else {
      this.stop()
    }
  }

  // A scroll of the row, by a control, by the timer or by a finger: the photo
  // in view becomes the current one.
  settle(): void {
    const index = indexAt(this.trackTarget.scrollLeft, this.slideWidth(), this.slideTargets.length)
    if (this.target !== null) {
      if (index !== this.target) return

      this.letGo()
    }
    if (index === this.index) return

    this.index = index
    this.mark()
  }

  private go(index: number, automatic = false): void {
    this.trackTarget.setAttribute("aria-live", automatic ? "off" : "polite")
    this.index = index
    this.mark()
    this.letGo()
    this.target = index
    this.targetTimer = window.setTimeout(this.targetExpired, 1000)
    const smooth = automatic || !this.motion?.matches
    this.trackTarget.scrollTo({ left: index * this.slideWidth(), behavior: smooth ? "smooth" : "auto" })
  }

  private mark(): void {
    this.slideTargets.forEach((slide, index) => {
      if (index === this.index) slide.removeAttribute("aria-hidden")
      else slide.setAttribute("aria-hidden", "true")
    })
    this.dotTargets.forEach((dot, index) => {
      if (index === this.index) dot.setAttribute("aria-current", "true")
      else dot.removeAttribute("aria-current")
    })
  }

  private slideWidth(): number {
    return this.slideTargets[0]?.offsetWidth || this.trackTarget.clientWidth
  }

  private stop(): void {
    this.stopped = true
    this.clearTimer()
    this.trackTarget.setAttribute("aria-live", "polite")
    this.paintPause()
  }

  private autoplay(): boolean {
    return shouldAutoplay(this.slideTargets.length, this.motion?.matches ?? true)
  }

  private schedule(): void {
    this.clearTimer()
    if (this.stopped || this.holds.size > 0 || !this.autoplay()) return

    this.trackTarget.setAttribute("aria-live", "off")
    this.timer = window.setTimeout(this.tick, AUTOPLAY_INTERVAL_MS)
  }

  private clearTimer(): void {
    if (this.timer !== null) window.clearTimeout(this.timer)
    this.timer = null
  }

  private paintPause(): void {
    if (!this.hasPauseTarget) return

    const pause = this.pauseTarget
    pause.hidden = !this.autoplay()
    pause.dataset.playing = String(!this.stopped)
    const label = this.stopped ? pause.dataset.labelPlay : pause.dataset.labelPause
    if (label) pause.setAttribute("aria-label", label)
  }

  private letGo(): void {
    if (this.targetTimer !== null) window.clearTimeout(this.targetTimer)
    this.targetTimer = null
    this.target = null
  }

  private readonly targetExpired = (): void => {
    this.letGo()
    this.settle()
  }

  private hold(reason: Hold): void {
    this.holds.add(reason)
    this.clearTimer()
  }

  private release(reason: Hold): void {
    this.holds.delete(reason)
    this.schedule()
  }

  private readonly tick = (): void => {
    this.timer = null
    this.go(nextIndex(this.index, this.slideTargets.length), true)
    this.schedule()
  }

  private readonly motionChanged = (): void => {
    if (!this.autoplay()) this.clearTimer()
    this.paintPause()
    this.schedule()
  }

  private readonly pointerEntered = (): void => this.hold("pointer")
  private readonly pointerLeft = (): void => this.release("pointer")
  private readonly focusEntered = (): void => this.hold("focus")

  private readonly focusLeft = (event: FocusEvent): void => {
    if (event.relatedTarget instanceof Node && this.element.contains(event.relatedTarget)) return

    this.release("focus")
  }

  private readonly takenInHand = (): void => {
    this.letGo()
    this.stop()
  }

  private readonly visibilityChanged = (): void => {
    if (document.hidden) this.hold("hidden")
    else this.release("hidden")
  }
}
